import { firstValueFrom } from "rxjs";
import { Injectable, inject } from "@angular/core";
import { Request, UnaryInterceptor, UnaryResponse } from "grpc-web";
import { SharedService } from "./shared.service";
import { TokenManagerService, LogoutContext } from "./token-manager.service";

type RequestType = Request<any, any>;
type InvokerType = (request: RequestType) => Promise<UnaryResponse<any, any>>;

/**
 * AuthGrpcInterceptor - Interceptor de autenticação para gRPC
 *
 * Este interceptor agora usa o TokenManagerService centralizado,
 * garantindo que não haja race condition entre HTTP e gRPC.
 *
 * IMPORTANTE: Este interceptor deve ser injetado como singleton,
 * não criado com `new AuthGrpcInterceptor()`.
 */
@Injectable({
  providedIn: "root",
})
export class AuthGrpcInterceptor implements UnaryInterceptor<RequestType, InvokerType> {
  protected shared = inject(SharedService);
  protected tokenManager = inject(TokenManagerService);

  intercept(request: RequestType, invoker: InvokerType) {
    const token = this.tokenManager.accessToken;
    if (token) {
      request.getMetadata()["Authorization"] = "Bearer " + token;
    }

    return invoker(request).catch(async (error: any) => {
      // gRPC error codes que indicam problema de autenticação:
      // 16 = UNAUTHENTICATED
      // 4 = DEADLINE_EXCEEDED (pode ser relacionado a token)
      // 401 = HTTP status code (alguns proxies retornam isso)
      // 0 = OK/CANCELLED (em alguns casos de erro de auth)
      if (error.code === 16 || error.code === 4 || error.code === 401 || error.code === 0) {
        console.log(`[AUTH gRPC] Auth error detected (code: ${error.code}), attempting refresh`);
        return this.handle401UnaryError(request, invoker, error);
      }
      throw error;
    });
  }

  private async handle401UnaryError(request: RequestType, invoker: InvokerType, originalError: any): Promise<UnaryResponse<any, any>> {
    // Usa o TokenManager centralizado - ele cuida do lock/queue automaticamente
    try {
      const refreshResponse = await firstValueFrom(this.shared.refresh("GRPC"));

      if (refreshResponse.status) {
        // Refresh bem-sucedido - atualiza o token e refaz a requisição
        const newToken = this.tokenManager.accessToken;
        console.log(`[AUTH gRPC] Refresh successful, retrying request`);
        request.getMetadata()["Authorization"] = "Bearer " + newToken;
        return invoker(request);
      } else {
        // Refresh falhou - faz logout com action específica
        const action = refreshResponse.action || "GRPC_AUTH_FAILED";
        const context: LogoutContext = {
          ...refreshResponse.context,
          source: "GRPC",
          originalError: {
            code: originalError.code,
            message: originalError.message,
          },
        };

        console.error(`[AUTH gRPC] Refresh failed, logging out`, { action, context });
        this.shared.logout(action, context).subscribe();

        throw new Error(`Token refresh failed: ${action}`);
      }
    } catch (err: any) {
      // Se o erro já foi tratado (já tem action), apenas propaga
      if (err.message?.startsWith("Token refresh failed:")) {
        throw err;
      }

      // Erro inesperado durante refresh
      const context: LogoutContext = {
        source: "GRPC",
        originalError: err?.message || err,
        hadRefreshToken: this.tokenManager.tokenState.isValid,
      };

      console.error(`[AUTH gRPC] Unexpected error during refresh`, context);
      this.shared.logout("GRPC_AUTH_FAILED", context).subscribe();

      throw err;
    }
  }
}
